import {
  HomeOutlined,
  UnorderedListOutlined,
  HourglassOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  BarChartOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { SidebarItem } from '_/components/common/layout/AppSidebar';
import { useCollection } from '_/contexts/CollectionContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSnapshotContext } from '_/contexts/SnapshotContext';
import { useSourceContext } from '_/contexts/SourceContext';
import { CRAWL_RUN_ACTIVE_STATUSES, IMPORT_IN_PROGRESS_SNAPSHOT_STATUSES } from '_/features/snapshots/common/snapshot.constants';
import React, { useMemo } from 'react';

// Create icon elements once to prevent re-renders
const homeIcon = <HomeOutlined />;
const unorderedListIcon = <UnorderedListOutlined />;
const hourglassIcon = <HourglassOutlined />;
const checkCircleIcon = <CheckCircleOutlined />;
const swapIcon = <SwapOutlined />;
const barChartIcon = <BarChartOutlined />;
const searchIcon = <SearchOutlined />;

export function useSnapshotSidebarItems(): SidebarItem[] {
  const { selectedOrgSlug } = useOrganization();
  const { selectedProjectSlug } = useProject();
  const { selectedCollectionSlug } = useCollection();
  const { selectedSourceSlug } = useSourceContext();
  const { snapshotId, selectedSnapshot, importStatus } = useSnapshotContext();

  return useMemo(() => {
    const shouldDisableImportStatusLink = !importStatus || importStatus.length === 0;
    const shouldDisableSidebarLinks =
      selectedSnapshot &&
      [...CRAWL_RUN_ACTIVE_STATUSES, ...IMPORT_IN_PROGRESS_SNAPSHOT_STATUSES].includes(selectedSnapshot.status);

    const items: SidebarItem[] = [
      {
        icon: homeIcon,
        text: 'Snapshot Home',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/home`,
      },
    ];

    // Add "Chunks" link if snapshot is chunked
    if (selectedSnapshot?.chunked) {
      items.push({
        icon: unorderedListIcon,
        text: 'Chunks',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/chunks`,
      });
    }

    items.push(
      {
        icon: hourglassIcon,
        text: 'Import status',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/importing`,
        disabled: shouldDisableImportStatusLink,
      },
      {
        icon: checkCircleIcon,
        text: 'Checks',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/tests`,
        disabled: shouldDisableSidebarLinks,
      },
      {
        icon: swapIcon,
        text: 'Compare',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/compare`,
        disabled: shouldDisableSidebarLinks,
      },
      {
        icon: barChartIcon,
        text: 'Graphs',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/graphs`,
        disabled: shouldDisableSidebarLinks,
      },
    );

    // Add "Drilldown" link if snapshot is not chunked
    if (!selectedSnapshot?.chunked) {
      items.push({
        icon: searchIcon,
        text: 'Drilldown',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/drilldown`,
        disabled: shouldDisableSidebarLinks,
      });
    }

    return items;
  }, [
    selectedOrgSlug,
    selectedProjectSlug,
    selectedCollectionSlug,
    selectedSourceSlug,
    snapshotId,
    selectedSnapshot,
    importStatus,
  ]);
}
